import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { paymentStatusService } from '../services/PaymentStatusService';

const Waiting = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [lastCheck, setLastCheck] = useState<Date | null>(null);

  useEffect(() => {
    if (!token) {
      console.warn('No token provided, redirecting to home');
      navigate('/');
      return;
    }

    const handleStatusUpdate = (status: string) => {
      console.log('Status update received:', { token, status });
      setLastCheck(new Date());

      switch (status.toLowerCase()) {
        case 'paid':
        case 'completed':
          console.log('Payment successful, redirecting to thank you page');
          navigate(`/merci?token=${token}`);
          break;
        case 'cancel':
        case 'canceled':
        case 'fail':
        case 'failed':
          console.log('Payment failed/canceled, redirecting to home');
          navigate('/');
          break;
        case 'pending':
          console.log('Payment still pending, continuing to wait');
          break;
        default:
          console.warn('Unknown payment status:', status);
          break;
      }
    };

    // Initial status check
    const checkInitialStatus = async () => {
      const initialStatus = await paymentStatusService.checkInitialStatus(token);
      handleStatusUpdate(initialStatus);
    };

    checkInitialStatus();

    // Subscribe to status updates
    paymentStatusService.subscribeToStatus(token, handleStatusUpdate);

    // Cleanup subscription
    return () => {
      paymentStatusService.unsubscribeFromStatus(token);
    };
  }, [navigate, token]);

  return (
    <>
      <Helmet>
        <title>Traitement de votre paiement - Couteau Suisse Digital</title>
        <meta name="description" content="Votre paiement est en cours de traitement. Merci de patienter." />
      </Helmet>

      <main className="min-h-screen py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-2xl shadow-xl p-8 text-center"
          >
            <motion.div
              animate={{
                scale: [1, 1.2, 1],
                rotate: [0, 360],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              className="w-24 h-24 bg-neon-orange/20 rounded-full mx-auto mb-8 flex items-center justify-center"
            >
              <svg className="w-12 h-12 text-neon-orange animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-3xl font-bold mb-4"
            >
              Traitement de votre paiement en cours
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="text-xl text-gray-600 mb-8"
            >
              Merci de ne pas fermer cette fenêtre pendant que nous traitons votre paiement.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="bg-gray-50 rounded-xl p-6 mb-8"
            >
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <motion.div
                    animate={{
                      scale: [1, 1.2, 1],
                    }}
                    transition={{
                      duration: 1,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                    className="w-3 h-3 bg-neon-orange rounded-full"
                  />
                  <span>Vérification du paiement</span>
                </div>
                <div className="flex items-center gap-3">
                  <motion.div
                    animate={{
                      scale: [1, 1.2, 1],
                    }}
                    transition={{
                      duration: 1,
                      delay: 0.3,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                    className="w-3 h-3 bg-neon-orange rounded-full"
                  />
                  <span>Préparation de votre commande</span>
                </div>
                <div className="flex items-center gap-3">
                  <motion.div
                    animate={{
                      scale: [1, 1.2, 1],
                    }}
                    transition={{
                      duration: 1,
                      delay: 0.6,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                    className="w-3 h-3 bg-neon-orange rounded-full"
                  />
                  <span>Configuration de votre espace client</span>
                </div>
              </div>
            </motion.div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 1 }}
              className="text-sm text-gray-500"
            >
              Cette opération peut prendre quelques instants. Vous serez automatiquement redirigé une fois le paiement confirmé.
              {lastCheck && (
                <span className="block mt-2 text-xs text-gray-400">
                  Dernière vérification : {lastCheck.toLocaleTimeString()}
                </span>
              )}
            </motion.p>
          </motion.div>
        </div>
      </main>
    </>
  );
};

export default Waiting;